"use client"

import {
    ArrowDown01Icon,
    ArrowUp01Icon,
    ArrowUpRightIcon,
} from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import Image from "next/image"
import Link from "next/link"
import { useId, useState } from "react"
import {
    Modal,
    ModalBody,
    ModalContent,
    ModalTrigger,
} from "@/components/ui/animated-modal"
import { trackProjectClick, trackProjectVisit } from "@/lib/analytics"
import type { PROJECTS_DATA } from "@/lib/projects-data"

export function ProjectDialog({
    project,
    children,
    onClick,
    onMouseEnter,
    onMouseLeave,
}: {
    project: (typeof PROJECTS_DATA)[0]
    children: React.ReactNode
    onClick?: () => void
    onMouseEnter?: () => void
    onMouseLeave?: () => void
}) {
    const [technologiesOpen, setTechnologiesOpen] = useState(false)
    const technologiesPanelId = useId()
    const titleId = useId()

    return (
        <Modal>
            <ModalTrigger
                className="block h-full w-full border-none bg-transparent p-0 text-left"
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {children}
            </ModalTrigger>
            <ModalBody
                labelledBy={titleId}
                className="w-full max-w-[95%] rounded-none! border border-border/70 bg-background/95 backdrop-blur-xl lg:max-w-[80%] xl:max-w-[70%] dark:border-border/40"
            >
                <ModalContent className="p-0">
                    <div
                        className="grid max-h-[75vh] min-h-[50vh] grid-cols-1 gap-0 overflow-y-auto md:grid-cols-2"
                        data-lenis-prevent="true"
                    >
                        {/* Left Column */}
                        <div
                            className="flex flex-col overflow-y-auto border-border/70 border-b bg-muted/5 p-6 [-ms-overflow-style:none] [scrollbar-width:none] md:border-r md:border-b-0 md:p-10 dark:border-border/40 [&::-webkit-scrollbar]:hidden"
                            data-lenis-prevent="true"
                        >
                            <div className="mb-8">
                                <h2
                                    id={titleId}
                                    className="mb-3 font-medium text-3xl text-foreground uppercase leading-tight tracking-widest md:text-4xl"
                                >
                                    {project.title}
                                </h2>
                                <span className="inline-block border border-border/70 bg-background/50 px-3 py-1 font-mono text-muted-foreground text-xs uppercase tracking-widest dark:border-border/40">
                                    {project.category}
                                </span>
                            </div>
                            {project.images?.[0] ? (
                                <div className="relative mb-auto aspect-16/12 w-full border border-border/70 bg-background/50 dark:border-border/40">
                                    <Image
                                        src={project.images[0]}
                                        alt={project.title}
                                        fill
                                        className="object-contain"
                                    />
                                </div>
                            ) : (
                                <div className="mt-auto flex aspect-video w-full flex-col items-center justify-center border border-muted-foreground/20 border-dashed bg-muted/5">
                                    <p className="font-mono text-muted-foreground text-sm">
                                        NO IMAGE
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Right Column */}
                        <div
                            className="flex flex-col overflow-y-auto p-6 [-ms-overflow-style:none] [scrollbar-width:none] md:p-10 [&::-webkit-scrollbar]:hidden"
                            data-lenis-prevent="true"
                        >
                            <div className="space-y-10">
                                <section>
                                    <h3 className="mb-4 flex items-center gap-2 border-border/70 border-b pb-2 font-mono text-foreground text-xs uppercase tracking-widest dark:border-border/40">
                                        <span className="h-1.5 w-1.5 rounded-none bg-foreground"></span>
                                        Overview
                                    </h3>
                                    <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
                                        {project.details.overview ||
                                            project.description}
                                    </p>
                                </section>

                                {project.details.features &&
                                    project.details.features.length > 0 && (
                                        <section>
                                            <h3 className="mb-4 flex items-center gap-2 border-border/70 border-b pb-2 font-mono text-foreground text-xs uppercase tracking-widest dark:border-border/40">
                                                <span className="h-1.5 w-1.5 rounded-none bg-foreground"></span>
                                                Key Features
                                            </h3>
                                            <ul className="space-y-3">
                                                {project.details.features.map(
                                                    (feature: string) => (
                                                        <li
                                                            key={feature}
                                                            className="flex items-start gap-3 text-muted-foreground text-sm"
                                                        >
                                                            <span className="mt-0.5 font-mono text-muted-foreground text-xs">
                                                                {(
                                                                    project.details.features?.indexOf(
                                                                        feature
                                                                    ) + 1
                                                                )
                                                                    .toString()
                                                                    .padStart(
                                                                        2,
                                                                        "0"
                                                                    )}
                                                            </span>
                                                            <span>
                                                                {feature}
                                                            </span>
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </section>
                                    )}

                                {project.details.technologies &&
                                    project.details.technologies.length > 0 && (
                                        <section>
                                            <button
                                                type="button"
                                                className="mb-4 flex w-full items-center justify-between gap-2 border-border/70 border-b pb-2 text-left font-mono text-foreground text-xs uppercase tracking-widest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:border-border/40"
                                                aria-expanded={technologiesOpen}
                                                aria-controls={
                                                    technologiesPanelId
                                                }
                                                onClick={() =>
                                                    setTechnologiesOpen(
                                                        (open) => !open
                                                    )
                                                }
                                            >
                                                <span className="flex min-w-0 items-center gap-2">
                                                    <span className="h-1.5 w-1.5 shrink-0 rounded-none bg-foreground" />
                                                    Technologies
                                                </span>
                                                <HugeiconsIcon
                                                    icon={
                                                        technologiesOpen
                                                            ? ArrowUp01Icon
                                                            : ArrowDown01Icon
                                                    }
                                                    size={16}
                                                    aria-hidden
                                                    className="shrink-0 text-muted-foreground"
                                                />
                                            </button>
                                            {technologiesOpen && (
                                                <div
                                                    className="flex flex-wrap gap-2"
                                                    id={technologiesPanelId}
                                                >
                                                    {project.details.technologies.map(
                                                        (tech: string) => (
                                                            <span
                                                                key={tech}
                                                                className="border border-border/70 bg-background px-3 py-1.5 font-mono text-muted-foreground text-xs uppercase tracking-wider dark:border-border/40"
                                                            >
                                                                {tech}
                                                            </span>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </section>
                                    )}

                                <div className="mt-auto flex flex-wrap gap-4 pt-6">
                                    {project.details.liveUrl && (
                                        <Link
                                            href={project.details.liveUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => {
                                                trackProjectClick(project.title)
                                                trackProjectVisit(
                                                    project.title,
                                                    project.details.liveUrl
                                                )
                                            }}
                                            className="group flex min-w-[200px] items-center justify-between gap-4 border border-foreground bg-foreground px-6 py-4 font-mono text-background text-xs uppercase tracking-widest transition-all hover:bg-foreground/90"
                                        >
                                            Visit Live Project
                                            <HugeiconsIcon
                                                icon={ArrowUpRightIcon}
                                                size={16}
                                                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                            />
                                        </Link>
                                    )}
                                    {project.details.githubUrl && (
                                        <Link
                                            href={project.details.githubUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => {
                                                trackProjectClick(project.title)
                                                trackProjectVisit(
                                                    project.title,
                                                    project.details.githubUrl
                                                )
                                            }}
                                            className="group flex min-w-[200px] items-center justify-between gap-4 border border-border/70 bg-transparent px-6 py-4 font-mono text-foreground text-xs uppercase tracking-widest transition-all hover:border-foreground dark:border-border/40"
                                        >
                                            View Source Code
                                            <HugeiconsIcon
                                                icon={ArrowUpRightIcon}
                                                size={16}
                                                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                            />
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </ModalContent>
            </ModalBody>
        </Modal>
    )
}
